import { useEffect, useRef, useState } from 'react'
import { Sheet, Button } from './UI'
import { useStageSlot, STAGE_PRIORITY } from '../lib/celebrationQueue'
import {
  HIGH_POINTS, isWorthAsking, isEligible, recordAsked, recordOutcome, openPlayListing,
} from '../lib/playPrompt'
import { logEvent } from '../lib/analytics'

// ── PlayRatingPrompt ──────────────────────────────────────────────────────────
// "You just did something great, would you tell Google Play?" — shown ONLY to
// Android users who installed from Play, and only riding on the back of a real
// high point (see playPrompt.js for the gate and the list of moments).
//
// IT TAKES THE STAGE, IT DOESN'T STEAL IT. The celebration queue already exists
// precisely so a Marquee, an achievement and a rating prompt can't fire on top
// of each other, and it already reserves a `rating` priority below both. So this
// arms on the high-point event and then WAITS: the card reveal plays, the final
// cut rolls, and only once the stage is free does the sheet slide up. Asking for
// a favour over the top of the moment you're trading on would waste both.
//
// The 900ms beat after the grant is deliberate. Landing the instant a cinematic
// clears reads as though the celebration was the setup for the ask; a breath
// makes it read as a separate, quieter question.
export function PlayRatingPrompt() {
  const [armed, setArmed] = useState(false)
  const [visible, setVisible] = useState(false)
  const asked = useRef(false)          // one ask per session, no matter how many high points land
  const viaRef = useRef('')            // which high point armed it, for the telemetry

  useEffect(() => {
    if (asked.current) return
    const onHighPoint = (e) => {
      if (asked.current) return
      if (!isWorthAsking(e.type, e.detail)) return
      if (!isEligible()) return
      asked.current = true
      viaRef.current = e.type.replace('nextep:', '')
      setArmed(true)
    }
    HIGH_POINTS.forEach(n => window.addEventListener(n, onHighPoint))
    return () => HIGH_POINTS.forEach(n => window.removeEventListener(n, onHighPoint))
  }, [])

  const granted = useStageSlot(armed, STAGE_PRIORITY.rating)

  useEffect(() => {
    if (!granted) return
    const t = setTimeout(() => { setVisible(true); recordAsked(); logEvent('play_prompt_shown', { via: viaRef.current }) }, 900)
    return () => clearTimeout(t)
  }, [granted])

  if (!granted || !visible) return null

  const close = (outcome) => {
    recordOutcome(outcome)
    logEvent('play_prompt_answered', { outcome, via: viaRef.current })
    setVisible(false)
    setArmed(false)          // releases the stage for whatever queued behind us
  }

  return (
    <Sheet onClose={() => close('later')} label="Rate NextEp on Google Play" from="bottom" width={420}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 10 }} aria-hidden="true">⭐</div>
        <h2 style={{
          fontFamily: 'var(--font-display, var(--font))', fontSize: 20, fontWeight: 700,
          color: 'var(--text-strong)', margin: '0 0 8px',
        }}>
          Enjoying NextEp?
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-dim)', margin: '0 0 18px' }}>
          It&rsquo;s built by one person, and a rating on Google Play is genuinely the
          single most useful thing you can do to help other people find it.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          <Button
            variant="primary"
            className="ui-primary"
            onClick={() => { openPlayListing(); close('rated') }}
          >
            Rate it on Play
          </Button>
          <Button variant="ghost" onClick={() => close('later')}>Maybe later</Button>
          <button
            type="button"
            onClick={() => close('never')}
            style={{
              background: 'none', border: 0, padding: '6px 0', cursor: 'pointer',
              fontFamily: 'var(--font)', fontSize: 12.5, color: 'var(--text-dim)',
              opacity: 0.75, textDecoration: 'underline',
            }}
          >
            Don&rsquo;t ask again
          </button>
        </div>
      </div>
    </Sheet>
  )
}
